import React, { useEffect, useRef, useState } from 'react';

import { endOfDay, startOfDay } from 'date-fns';
import { DateRangePicker } from 'react-date-range';
import CalendarIcon from '../../../../icons/CalendarIcon';
import {
  formatDateRangeFilter,
  getSearchQuickRanges,
} from '../../../../lib/search';
import css from './SearchDateFilter.module.css';

const CONTAINER_WIDTH = 580;

const parseDate = (date) => {
  if (!date) return null;
  return date instanceof Date ? date : new Date(date);
};

const getBounds = (ref) => {
  if (!ref || !ref.current)
    return {
      top: 0,
      height: 0,
      left: 0,
      width: 0,
      bottom: 0,
      right: 0,
    };
  return ref.current.getBoundingClientRect();
};

const CloseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 11 11">
    <path
      d="M0.77933 8.87179C0.40689 9.24424 0.40689 9.84809 0.77933 10.2205C1.15177 10.593 1.75561 10.593 2.12805 10.2205L5.49994 6.84861L8.87195 10.2207C9.24439 10.5931 9.84823 10.5931 10.2207 10.2207C10.5931 9.84822 10.5931 9.24437 10.2207 8.87192L6.84866 5.49986L10.2204 2.12808C10.5928 1.75563 10.5928 1.15178 10.2204 0.779334C9.84795 0.406889 9.24411 0.406889 8.87167 0.779334L5.49994 4.15112L2.12833 0.779462C1.75589 0.407017 1.15205 0.407017 0.779606 0.779462C0.407166 1.15191 0.407166 1.75576 0.779606 2.12821L4.15121 5.49986L0.77933 8.87179Z"
      fill="#131616"
    />
  </svg>
);

const SearchDateFilter = ({
  isVisible,
  isDisabled,
  dateInterval,
  setValue,
  customClass,
  showRight,
}) => {
  const ref = useRef();
  const pickerRef = useRef();

  const [isOpen, setOpen] = useState(false);

  const dates = {
    startDate: parseDate(dateInterval[0]),
    endDate: parseDate(dateInterval[1]),
  };

  const [focusRange, setFocusRange] = useState([0, 0]);

  useEffect(() => {
    const scrollHandler = () => {
      const root = document.documentElement;
      const { top, height, left, width } = getBounds(ref);
      const xCoord = showRight ? left - CONTAINER_WIDTH : left + width;
      const yCoord = top + height / 2;
      const { height: pickerHeight } = getBounds(pickerRef);
      const xOffset = xCoord + 10;
      const yOffset = yCoord - pickerHeight * 0.333;
      root.style.setProperty('--search-date-x', `${xOffset}px`);
      root.style.setProperty('--search-date-y', `${yOffset}px`);
    };
    scrollHandler();
    document.addEventListener('scroll', scrollHandler);
    return () => document.removeEventListener('scroll', scrollHandler);
  }, [isOpen]);

  useEffect(() => {
    const handler = (event) => {
      const clickDateFilter = ref.current && ref.current.contains(event.target);
      if (!clickDateFilter) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const dateSelected = dates.startDate && dates.endDate;

  const isStaticDate = (date) => {
    return new Date(date).getMilliseconds() !== 0;
  };

  const handleDateSelect = (dateRangeInfo) => {
    // Weird behavior: Sometimes it's reported as { selection: <value> } and others as { range1: <value> }
    const key = Object.keys(dateRangeInfo)[0];
    const { [key]: dateRange } = dateRangeInfo;
    const { startDate, endDate } = dateRange;

    if (isStaticDate(startDate)) {
      setValue([startDate, endDate]);
      return;
    }

    setValue([startOfDay(startDate), endOfDay(endDate)]);
  };

  const handleFocusChange = (rangeItem) => {
    // Check for [0, 1] if you want to close the input on range select
    setFocusRange(rangeItem);
  };

  return (
    <div
      ref={ref}
      className={`${css.main} ${customClass}`}
      data-visible={isVisible ? 'visible' : 'hidden'}
      data-cy="search-date-filter"
    >
      <div
        className={css.label}
        data-status={dateSelected ? 'value' : 'empty'}
        data-disabled={isDisabled}
        onClick={() => setOpen((c) => !c)}
      >
        {!dateSelected && (
          <span className={css.placeholder}>
            <CalendarIcon className={css.placeholderIcon} />
            Select date
          </span>
        )}
        {dateSelected && (
          <div className={css.value}>
            <span className={css.valueDate}>
              {formatDateRangeFilter(dates)}
            </span>
            <button
              type="button"
              className={css.closeButton}
              onClick={() => setValue([null, null])}
              data-cy="search-date-filter-tag-remove"
            >
              <CloseIcon className={css.close} />
            </button>
          </div>
        )}
      </div>
      {isOpen && (
        <div ref={pickerRef} className={css.datePicker}>
          <DateRangePicker
            className={css.dateRange}
            startDatePlaceholder="Start date"
            endDatePlaceholder="End date"
            onChange={handleDateSelect}
            focusedRange={focusRange}
            onRangeFocusChange={handleFocusChange}
            showSelectionPreview
            moveRangeOnFirstSelection={false}
            months={1}
            ranges={[dates]}
            direction="horizontal"
            rangeColors={['#f3f3f3']}
            weekStartsOn={1}
            staticRanges={getSearchQuickRanges()}
            inputRanges={[]}
            preventSnapRefocus
          />
        </div>
      )}
    </div>
  );
};

export default SearchDateFilter;
